import { assertCompleteTask, queryKeys, SCHEMA_NAMES, sdk } from 'utils/query';
import { t } from 'i18next';
import { displayNotificationError, displayNotificationLoading, displayNotificationSuccess } from 'utils/notifications';
import { PrescriptionDeactivateError } from 'services/errors/PrescriptionDeactivateError';
import { rqlBuilder } from '@extrahorizon/javascript-sdk';
import retry from 'async-retry';
import { showFullName } from 'utils/helpers';
import { PatientProfile } from 'utils/models';
import { PatientData } from 'models/user';
import { useCustomMutation } from '../useCustomMutation';

interface DeactivatePrescriptionProps {
  groupIds: string[];
  patient: PatientProfile | PatientData;
}

const deactivatePrescriptionForGroup = async (groupId: string, patient: PatientProfile | PatientData, now: Date) => {
  const result = await sdk.tasks.functions.execute('integration', {
    command: 'cancel-prescription',
    data: { groupId, userId: patient.id },
  });

  if (!assertCompleteTask({ result })) throw new PrescriptionDeactivateError();

  await retry(
    async () => {
      const rql = rqlBuilder().eq('data.user.id', patient.id!).eq('data.group.id', groupId).build();

      const user = await sdk.data.documents.findFirst(SCHEMA_NAMES.physicianDashboardTableRows, {
        rql,
      });

      if (user && user.updateTimestamp > now) return;
      throw new PrescriptionDeactivateError();
    },
    {
      retries: 15,
      maxTimeout: 2000,
    },
  );
};

export const deactivatePrescriptions = async ({ groupIds, patient }: DeactivatePrescriptionProps) => {
  const notificationId = displayNotificationLoading({
    title: t('deactivating_prescriptions_label'),
    message: t('deactivating_prescriptions_loading_label'),
  });

  if (groupIds.length === 0) {
    displayNotificationError({ notificationId, error: new PrescriptionDeactivateError() });
    return;
  }

  const now = new Date();
  // Groups are independent, so run them concurrently and let every group finish (success or
  // failure) rather than aborting the rest as soon as one fails.
  const results = await Promise.allSettled(
    groupIds.map((groupId) => deactivatePrescriptionForGroup(groupId, patient, now)),
  );

  if (results.some((result) => result.status === 'rejected')) {
    displayNotificationError({
      notificationId,
      error: new PrescriptionDeactivateError(),
    });
    return;
  }

  displayNotificationSuccess({
    notificationId,
    message: t('prescript_deactivated_notification', { patientName: showFullName(patient) }),
  });
};

export const useDeactivatePrescriptions = () => {
  return useCustomMutation({
    fn: deactivatePrescriptions,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.patients], [queryKeys.patientData]],
    },
  });
};
